import { type QueryByModelResp, queryCataloglUsingPost } from '@/api/queryCatalog';
import ResizableBox from '@/components/ResizableBox';
import { useRequest } from 'ahooks';
import { message, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import CatalogTree, { type SelectData } from './CatalogTree';
import styles from './index.module.less';
import VehicleInfo from './VehicleInfo';
import LegendList from './LegendList';
import LegendDetail from './LegendDetail';
import Search, { type SearchRef } from './Search';
import { DetailCard, useAutoHeight } from 'xui-pro';
import emptyImg from './assets/empty.svg';
import { useQuery } from '@xpeng/xdragon-ass-components-eu';

export type PartsCatalogDetailProps = {
  /**
   * 车型编码。modelCode / vin 二者至少传其一
   */
  modelCode?: string;
  /**
   * 车架号。modelCode / vin 二者至少传其一
   */
  vin?: string;
  /**
   * 是否作为Tab嵌入使用，默认false。
   * 为true时不再通过 window.xDragonBridge.updateTab 更新浏览器Tab标题（该行为属于EPC独立页面场景），
   * 避免嵌入到宿主详情页Tab后错误地覆盖宿主Tab标题
   */
  embedded?: boolean;
};

const PartsCatalog = (props: PartsCatalogDetailProps) => {
  const { embedded = false } = props;
  const { legendCode: queryLegendCode, partNo: queryPartNo, directPart } = useQuery();

  // modelCode/vin/legendCode/partNo 合并为单一状态对象维护（而非4个独立useState），
  // 确保搜索命中新车型/VIN时通过一次setState整体更新，避免被拆分成多次渐进渲染
  // ——React 17在事件外的异步回调中不会自动合并多个setState，拆分渲染会导致中间出现
  // "新车型+旧VIN"这类错误的参数组合，进而触发一次多余且参数错误的查询，
  // 该错误查询若晚于正确查询返回，会用错误数据覆盖已正确展示的结果。
  // 同时不触发路由跳转，避免影响宿主Tab/浏览器地址。初始值取自props/URL query，后续搜索仅整体更新该状态
  const [locateState, setLocateState] = useState(() => ({
    modelCode: props.modelCode,
    vin: props.vin,
    legendCode: queryLegendCode as string | undefined,
    partNo: queryPartNo as string | undefined,
    // seq 每次搜索定位都自增，即使车型/VIN/图例/备件与上一次完全相同（如重复点击查询同一车型），
    // 也能让下方 CatalogTree 的 key 发生变化并重新挂载，恢复"自动选中目录"效果
    seq: 0
  }));
  const {
    modelCode,
    vin,
    legendCode: locateLegendCode,
    partNo: locatePartNo,
    seq: locateSeq
  } = locateState;
  useEffect(() => {
    setLocateState(prev => ({ ...prev, modelCode: props.modelCode, vin: props.vin }));
  }, [props.modelCode, props.vin]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<QueryByModelResp>();
  const [currentSelect, setCurrentSelect] = useState<SelectData>();
  const { _height } = useAutoHeight(containerRef, true, undefined, 395, false, 8);
  const searchRef = useRef<SearchRef>(null);

  const handleSearchLocate = useCallback(
    (nextModelCode?: string, nextVin?: string, nextLegendCode?: string, nextPartNo?: string) => {
      setCurrentSelect(undefined);
      setLocateState(prev => ({
        modelCode: nextModelCode,
        vin: nextVin,
        legendCode: nextLegendCode,
        partNo: nextPartNo,
        seq: prev.seq + 1
      }));
    },
    []
  );

  const { loading } = useRequest(
    async () => {
      if (!vin && !modelCode) return;
      const res = await queryCataloglUsingPost({
        vin,
        modelCode,
        directPart: Number(directPart)
      });
      if (res.code === 200) {
        if (!embedded) {
          if (modelCode) {
            window.xDragonBridge.updateTab({
              path: location.pathname.replace('/main', ''),
              title: `EPC${`-${res.data.vehicleInfo?.modelName ?? ''}`}`
            });
          } else if (vin) {
            window.xDragonBridge.updateTab({
              path: location.pathname.replace('/main', ''),
              title: `EPC${`-${vin.substring(vin.length - 6)}`}`
            });
          }
        }
        setData(res.data);
      } else {
        message.error(res.msg);
      }
    },
    { manual: false, refreshDeps: [modelCode, vin] }
  );
  const [showCatalog, setShowCatalog] = useState(true);
  const [showPartDetail, setShowPartDetail] = useState(false);
  const handlerPull = useCallback(() => {
    setShowCatalog(false);
  }, []);
  const handlePush = useCallback(() => {
    setShowCatalog(true);
    setShowPartDetail(false);
  }, []);

  return (
    <>
      <Search ref={searchRef} modelCode={modelCode} vin={vin} onSearchLocate={handleSearchLocate} />
      <div
        className={`${styles['main']} ${styles['container']}`}
        ref={containerRef}
        style={{ height: `${_height}px` }}
      >
        {_height &&
          (data && data.catalog?.length ? (
            <>
              <ResizableBox
                direction='right'
                width={!showCatalog ? 0 : 240}
                minWidth={!showCatalog ? 0 : 160}
                maxWidth={!showCatalog ? 0 : 340}
                style={{ height: '100%', overflowY: 'scroll', overflowX: 'hidden' }}
                disabled={!showCatalog}
              >
                {showCatalog ? (
                  <></>
                ) : (
                  <div className={`${styles['pull-push-box']} ${styles['pull-box']}`}>
                    <div className={styles['push-icon']} onClick={handlePush} />
                  </div>
                )}
                <div
                  className={styles['left-content']}
                  style={{ display: showCatalog ? 'block' : 'none' }}
                >
                  <VehicleInfo data={data.vehicleInfo} vin={vin} />
                  <DetailCard title='目录'>
                    <CatalogTree
                      key={`${data.vehicleInfo?.modelCode ?? ''}-${data.vehicleInfo?.vin ?? ''}-${
                        locateLegendCode ?? ''
                      }-${locatePartNo ?? ''}-${locateSeq}`}
                      modelCode={data.vehicleInfo?.modelCode}
                      onSelect={setCurrentSelect}
                      select={currentSelect}
                      data={data.catalog}
                      legendCode={locateLegendCode as string}
                      partNo={locatePartNo as string}
                    />
                  </DetailCard>
                </div>
                {showCatalog ? (
                  <div className={`${styles['pull-push-box']} ${styles['push-box']}`}>
                    <div className={styles['pull-icon']} onClick={handlerPull} />
                  </div>
                ) : (
                  <></>
                )}
              </ResizableBox>
              {!currentSelect && (
                <div className={styles['empty-center']}>
                  <img className={styles['empty-image']} src={emptyImg} alt='empty' />
                  <p className={styles['empty-text']}>暂无数据</p>
                </div>
              )}
              <div className={styles['right-content']}>
                {currentSelect?.groupCode && (
                  <LegendList
                    data={currentSelect}
                    onOpenLegendDetail={setCurrentSelect}
                    showCatalog={showCatalog}
                  />
                )}
                {currentSelect?.legendCode && (
                  <LegendDetail
                    modelCode={data.vehicleInfo!.modelCode!}
                    vin={data.vehicleInfo!.vin}
                    legendCode={currentSelect.legendCode}
                    partNo={currentSelect.partNo}
                    showCatalog={showCatalog}
                    showPartDetail={showPartDetail}
                    vehicleInfo={data.vehicleInfo}
                    onClosePartDetail={() => {
                      setShowPartDetail(false);
                      setShowCatalog(true);
                    }}
                    onOpenPartDetail={() => {
                      setShowCatalog(false);
                      setShowPartDetail(true);
                    }}
                    handlePartSearch={_partNo => searchRef.current?.onPartSearch(_partNo)}
                  />
                )}
              </div>
            </>
          ) : (
            <div className={styles['empty-center']}>
              {loading ? (
                <Spin />
              ) : (
                <>
                  <img className={styles['empty-image']} src={emptyImg} alt='empty' />
                  <p className={styles['empty-text']}>暂无数据</p>
                </>
              )}
            </div>
          ))}
      </div>
    </>
  );
};
export default PartsCatalog;
